import ENV from "app/utils/ENV";
import apiActions from "app/utils/apiActions";
import storageActions from "app/utils/storageActions";
import valueHelpers from "app/utils/valueHelpers";
import valueActions from "app/utils/valueActions";
import textHelpers from "app/utils/textHelpers";
import timeHelpers from "app/utils/timeHelpers";
import * as RootNavigation from "app/utils/RootNavigation.js";
import { Platform, Alert } from "react-native";

// const { google } = require("googleapis");
// const client = require("googleapis/build/src/apis/calendar");
// const auth = require("google-auth-library");

import store from "app/redux/store";

function base64UrlEncodeString(str) {
  const base64 = btoa(unescape(encodeURIComponent(str)));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function arrayBufferToBase64Url(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

function pemToArrayBuffer(pem) {
  const base64 = pem
    .replace("-----BEGIN PRIVATE KEY-----", "")
    .replace("-----END PRIVATE KEY-----", "")
    .replace(/\n/g, "");
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return buffer;
}

async function createServiceAccountJWT(clientEmail, privateKeyPem) {
  const now = Math.floor(Date.now() / 1000);
  const header = base64UrlEncodeString(JSON.stringify({ alg: "RS256", typ: "JWT" }));
  const payload = base64UrlEncodeString(JSON.stringify({
    iss: clientEmail,
    scope: "https://www.googleapis.com/auth/calendar",
    aud: "https://oauth2.googleapis.com/token",
    exp: now + 3600,
    iat: now,
  }));
  const signingInput = `${header}.${payload}`;
  const keyBuffer = pemToArrayBuffer(privateKeyPem);
  const key = await crypto.subtle.importKey(
    "pkcs8",
    keyBuffer,
    { name: "RSASSA-PKCS1-v1_5", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const signature = await crypto.subtle.sign(
    "RSASSA-PKCS1-v1_5",
    key,
    new TextEncoder().encode(signingInput),
  );
  return `${signingInput}.${arrayBufferToBase64Url(signature)}`;
}

let calendarActions = {
  async getEvents(personal = false, silent = false) {
    // let events = await calendarActions.getEventByCalendarId(undefined, silent); // default
    let events = [];
    let keys = [
      "p0rsslenmmnghtneu9htt791m4@group.calendar.google.com", // personal
      "salulos@gmail.com", // primary
      "en.usa#holiday@group.v.calendar.google.com", // holidays
    ];
    for (let key of keys) {
      let evts = await calendarActions.getEventByCalendarId(key, silent);
      events = [...events, ...evts];
    }
    events = textHelpers.uniq(events, "id");
    events = events.map((e) => ({ ...e, startAt: e.start?.dateTime }));
    events = textHelpers.newestFirst(events, "startAt");
    if (events.length > 0) {
      storageActions.saveItem("gCalEvents", events);
    }
    return events;
  },
  async getCachedEvents() {
    return (await storageActions.fetchItem("gCalEvents")) || [];
  },
  async dismissEvent(eventId) {
    let dismissed = (await storageActions.fetchItem("gCalDismissed")) || [];
    if (!dismissed.includes(eventId)) {
      await storageActions.saveItem("gCalDismissed", [...dismissed, eventId]);
    }
  },
  async getEventsForChapter(startAt, endAt) {
    let events = await calendarActions.getCachedEvents();
    let dismissed = (await storageActions.fetchItem("gCalDismissed")) || [];
    let startDate = startAt.substring(0, 10);
    let endDate = (endAt || startAt).substring(0, 10);
    return events.filter((e) => {
      if (dismissed.includes(e.id)) return false;
      let eventDate = e.startAt || e.start?.date;
      if (!eventDate) return false;
      let dateStr = eventDate.substring(0, 10);
      return dateStr >= startDate && dateStr <= endDate;
    });
  },
  async saveServiceAccount(json) {
    let parsed = typeof json === "string" ? JSON.parse(json) : json;
    await storageActions.saveItem("gCalClientEmail", parsed.client_email);
    await storageActions.saveItem("gCalPrivateKey", parsed.private_key);
    await storageActions.clearItem("gCalAccessToken");
  },
  async disconnect() {
    await storageActions.clearItem("gCalClientEmail");
    await storageActions.clearItem("gCalPrivateKey");
    await storageActions.clearItem("gCalAccessToken");
    await storageActions.clearItem("gCalEvents");
  },
  async isConnected() {
    let email = await storageActions.fetchItem("gCalClientEmail");
    return !!email;
  },
  async getAccessToken() {
    let cached = await storageActions.fetchItem("gCalAccessToken");
    if (cached && cached.expires_at > Date.now()) {
      return cached.access_token;
    }
    let clientEmail = await storageActions.fetchItem("gCalClientEmail");
    let privateKey = await storageActions.fetchItem("gCalPrivateKey");
    if (!clientEmail || !privateKey) return null;
    let jwt = await createServiceAccountJWT(clientEmail, privateKey);
    let res = await fetch("https://oauth2.googleapis.com/token", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: `grant_type=urn%3Aietf%3Aparams%3Aoauth%3Agrant-type%3Ajwt-bearer&assertion=${jwt}`,
    });
    let data = await res.json();
    if (data.access_token) {
      await storageActions.saveItem("gCalAccessToken", {
        access_token: data.access_token,
        expires_at: Date.now() + (data.expires_in - 60) * 1000,
      });
      return data.access_token;
    }
    return null;
  },
  async syncIfAuthenticated() {
    let connected = await calendarActions.isConnected();
    if (!connected) return [];
    return calendarActions.getEvents(false, true);
  },
  async getEventByCalendarId(calendarId, silent = false) {
    let accessToken = await calendarActions.getAccessToken();
    if (!accessToken) {
      if (!silent) alert("Please connect Google Calendar in Settings first");
      return [];
    }
    console.log("getting events...", calendarId);
    let timeMin = timeHelpers.daysFromNow(-10).toISOString();
    let timeMax = timeHelpers.daysFromNow(10).toISOString();
    let url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?timeMin=${timeMin}&timeMax=${timeMax}&showDeleted=true&maxResults=50`;
    let res = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    let data = await res.json();
    return data.items || [];
  },
  async get() {
    let pref = await apiActions.request("preferences/1");
    if (!!pref) {
      let preferences = {
        todoIds: pref.todo_ids,
        hiddenIds: pref.hidden_ids,
        focusIds: pref.focus_ids,
        dismissedTodos: pref.dismissed_todos,
      };
      store.dispatch({ type: "preferences/update", preferences });
      return preferences;
    }
  },
};

export default calendarActions;
